"use client";

/**
 * MetaRow — title + view counter + status chip + sig toggle button
 *
 * Layout (per PM directive 2026-05-23):
 *   Row 1: title on the right (RTL reading start) + status chip on the left.
 *   Row 2: signatures toggle on the right + view counter on the left.
 *
 * The signature-list dropdown is a shadcn Popover (Radix-backed). align="start"
 * lines its inline-start edge up with the toggle's inline-start edge (right
 * edge in RTL), so the dropdown grows inward. Radix handles outside-click
 * dismissal and Escape-to-close for free — no manual listeners.
 *
 * V1: toggle uses white stroke variant (accentVariant=false)
 * V2/V3: toggle uses accent stroke variant (accentVariant=true)
 */

import { Eye } from "iconsax-reactjs";
import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { StatusChipState } from "../shared/StatusChip";
import { StatusChip } from "../shared/StatusChip";
import { SignaturesToggleButton } from "./SignaturesToggleButton";
import { VoterDropdown } from "./VoterDropdown";

interface MetaRowProps {
  title: string;
  viewCount: number;
  numConfirmations: number;
  chipState: StatusChipState;
  accentVariant: boolean;
  txIndex: number;
  cycleCount: number;
}

export function MetaRow({
  title,
  viewCount,
  numConfirmations,
  chipState,
  accentVariant,
  txIndex,
  cycleCount,
}: MetaRowProps) {
  const [voterOpen, setVoterOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {/* Row 1: title (visual right via first-child + RTL) ↔ status chip (left). */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-lg font-bold text-text-primary break-words flex-1 min-w-0">
          {title}
        </h1>
        <div className="shrink-0">
          <StatusChip state={chipState} />
        </div>
      </div>

      {/* Row 2: signatures toggle (visual right) ↔ view counter (left). */}
      <div className="flex items-center justify-between gap-4">
        {/* With 0 signatures: render the toggle without the Popover wrapper
            so clicking it doesn't open an empty dropdown. Appearance is
            unchanged — Radix just isn't there to bind a click handler. */}
        {numConfirmations === 0 ? (
          <SignaturesToggleButton
            count={0}
            isOpen={false}
            accentVariant={accentVariant}
          />
        ) : (
          <Popover open={voterOpen} onOpenChange={setVoterOpen}>
            <PopoverTrigger asChild>
              <SignaturesToggleButton
                count={numConfirmations}
                isOpen={voterOpen}
                accentVariant={accentVariant}
              />
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={8}
              // Override the shadcn defaults that don't fit this menu: drop the
              // fixed 18rem width + 1rem padding, swap the popover surface for
              // our secondary bg, and hide the scrollbar inside the 16rem list.
              className="w-auto min-w-[240px] max-w-[320px] p-0 max-h-64 overflow-y-auto bg-bg-secondary text-text-primary border-stroke-light [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <VoterDropdown txIndex={txIndex} cycleCount={cycleCount} />
            </PopoverContent>
          </Popover>
        )}

        <div className="flex items-center gap-1 text-sm text-text-secondary">
          <Eye size={16} />
          <span dir="ltr">{viewCount}</span>
        </div>
      </div>
    </div>
  );
}
